/**
 * Presentation-layer claim guard.
 *
 * Some supplier-written product copy in Shopify contains absolute safety or
 * health claims ("myrkytön", "estää putoamisen", "ennaltaehkäisee selkäkipuja")
 * that we cannot substantiate and that Finnish consumer-protection rules do not
 * allow. Until the copy is corrected at source in Shopify, every product text
 * rendered on the site passes through these rewrites.
 *
 * Rules only ever weaken a claim — never add a new one.
 */

type Rule = [RegExp, string];

const RULES: Rule[] = [
  // Absolute non-toxicity claims → describe the finish, don't certify it.
  [/myrkyttöm(ät|iä)\s+vesiohenteise(t|ia)\s+laka(t|oja)/gi, "vesiohenteiset lakat"],
  [/myrkytön\s+vesiohenteinen\s+viimeistely/gi, "vesiohenteinen viimeistely"],
  [/myrkytön\s+(viimeistely|pintakäsittely)/gi, "vesiohenteinen $1"],
  [/myrkyttömäst[äa]\s+/gi, ""],
  [/\bmyrkyttöm(ät|iä|än|ää)\s+/gi, ""],
  [/,?\s*myrkytön\b/gi, ""],

  // Absolute fall / safety guarantees.
  [
    /estävät putoamisen,\s*vaikka taapero horjahtaisi/gi,
    "tukevat lasta työtason ääressä – aikuisen valvonta on silti aina tarpeen",
  ],
  [/estä(ä|vät) putoamisen/gi, "tuovat lisätukea"],
  [/takaa turvallisen leikkiympäristön/gi, "on helppo pyyhkiä puhtaaksi"],
  [/takaa turvallis\w*/gi, "on helppo pitää puhtaana"],
  [/liukumaton pinta/gi, "hiottu pinta"],
  [/turvallisen tavan harjoitella/gi, "vakaan alustan harjoitella"],
  [/turvallinen kiipeilykokemus/gi, "kiipeily omaan tahtiin aikuisen valvonnassa"],
  [/turvallisen ensikiipeilykokemuksen/gi, "matalan ensikiipeilykorkeuden"],
  [/nostaa lapsen keittiön työtasolle turvallisesti/gi, "nostaa lapsen keittiön työtason korkeudelle"],
  [/täysin turvallin\w*/gi, "huolellisesti suunniteltu"],

  // Health / medical claims.
  [/\s*Se parantaa luonnostaan ryhtiä sekä ennaltaehkäisee selkäkipuja ja nivelten kulumista\./gi, ""],
  [
    /Lisäksi tasapainopuomi stimuloi hermosoluja, jotka ovat erityisen tärkeitä lapsen jalkojen terveelle kehitykselle\./gi,
    "Tasapainoilu on monipuolista liikuntaa jalkojen ja keskivartalon lihaksille.",
  ],
  [/ennaltaehkäise\w*|ehkäisee\s+\w+kipuja/gi, "tukee"],
  [/\bparantaa keskittymistä/gi, "harjoittaa keskittymistä"],
  [/\bparantaa ryhtiä/gi, "kannustaa hyvään ryhtiin"],
];

/** Rewrite unsupportable claims in a plain-text product string. */
export function guardClaims(text?: string | null): string {
  if (!text) return "";
  let out = text;
  for (const [re, to] of RULES) out = out.replace(re, to);
  return out.replace(/\s{2,}/g, " ").replace(/\s+([,.])/g, "$1");
}

/** Same rewrites for HTML product copy (rules never match across tags). */
export function guardClaimsHtml(html?: string | null): string {
  if (!html) return "";
  let out = html;
  for (const [re, to] of RULES) out = out.replace(re, to);
  return out;
}
